import type {
  ObjectType,
  QueryModelType,
  FacetsRetrieve,
  Locale,
  SupportedCurrencies,
  Mode,
  GetNbHiddenHousesInput,
  SearchHousesMapInput,
  GetSimilarHousesPricesByPeriodInput,
} from '../../../types'
import type {
  GetAreaEquipmentsUseCase,
  GetHouseDetailsUseCase,
  GetHouseKeyFeaturesUseCase,
  GetHousePeriodsAndBookingsUseCase,
  GetHousePeriodsByIdUseCase,
  GetHousePeriodsUseCase,
  GetHousePhotosUseCase,
  GetNbHiddenHousesUseCase,
  GetTopDestinationsUseCase,
  MakeHouseRequestUseCase,
  OrderRetrievedFacetsUseCase,
  SearchHousesAndDestinationsUseCase,
  SearchHousesMapUseCase,
  SearchHousesWithDatesUseCase,
  SearchHousesWithoutDatesUseCase,
  SearchHousesWithoutDatesV2UseCase,
  SearchHousesSplitMapUseCase,
  GetHouseSplitMapUseCase,
  UpdateInquiryUseCase,
  GetHouseIncludedServices,
  GetHouseTruthLinksUseCase,
  GetDestinationFavoritePeriodUseCase,
  GetDestinationPhotoUseCase,
  GetSimilarHousesPricesByPeriodUseCase,
  SearchDestinationsUseCase,
  GetHouseNextValidPeriodUseCase,
  SearchUnknownDestinationUseCase,
} from '../usecases'
import type { GetHouseDetailsPresentation } from '../usecases/GetHouseDetails/interfaces/GetHouseDetailsPresentation'
import type { GetHousePeriodsPresentation } from '../usecases/GetHousePeriods/interfaces/GetHousePeriodsPresentation'
import type { GetHousePhotosPresentation } from '../usecases/GetHousePhotos/interfaces/GetHousePhotosPresentation'
import type { GetTopDestinationsPresentation } from '../usecases/GetTopDestinations/interfaces/GetTopDestinationsPresentation'
import type { MakeHouseRequestPresentation } from '../usecases/MakeHouseRequest/interfaces/MakeHouseRequestPresentation'
import type { OrderRetrievedFacetsPresentation } from '../usecases/OrderRetrievedFacets/interfaces/OrderRetrievedFacetsPresentation'
import type { SearchHousesPresentation } from '../usecases/SearchHouses/interfaces/SearchHousesPresentation'
import type { SearchHousesAndDestinationsPresentation } from '../usecases/SearchHousesAndDestinations/interfaces/SearchHousesAndDestinationsPresentation'
import type { GetHousePeriodsAndBookingsPresentation } from '../usecases/GetHousePeriodsAndBookings/interfaces/GetHousePeriodsAndBookingsPresentation'
import type { MakeHouseRequestInput } from '../usecases/MakeHouseRequest/types'
import type { UpdateInquiryInput } from '../usecases/UpdateInquiry/types'
import type { UpdateInquiryPresentation } from '../usecases/UpdateInquiry/interfaces/UpdateInquiryPresentation'
import type { GetNbHiddenHousesPresentation } from '../usecases/GetNbHiddenHouses/interfaces/GetNbHiddenHousesPresentation'
import type { SearchHousesMapPresentation } from '../usecases/SearchHousesMap/interfaces/SearchHousesMapPresentation'
import type { GetHouseKeyFeaturesPresentation } from '../usecases/GetHouseKeyFeatures/interfaces/GetHouseKeyFeaturesPresentation'
import type { GetAreaEquipmentsPresentation } from '../usecases/GetAreaEquipments/interfaces/GetAreaEquipmentsPresentation'
import type { SearchType } from '../../../services/algolia'
import type { GetHousePeriodsByIdPresentation } from '../usecases/GetHousePeriodsById/interfaces/GetHousePeriodsByIdPresentation'
import type { GetHouseIncludedServicesPresentation } from '../usecases/GetHouseIncludedServices/interfaces/GetHouseIncludedServicesPresentation'
import type { GetHouseTruthLinksPresentation } from '../usecases/GetHouseTruthLinks/interfaces/GetHouseTruthLinksPresentation'
import type { GetDestinationFavoritePeriodPresentation } from '../usecases/GetDestinationFavoritePeriod/interfaces/GetDestinationFavoritePeriodPresentation'
import type { GetDestinationPhotoPresentation } from '../usecases/GetDestinationPhoto/interfaces/GetDestinationPhotoPresentation'
import type { GetSimilarHousesPricesByPeriodPresentation } from '../usecases/GetSimilarHousesPricesByPeriod/interfaces/GetSimilarHousesPricesByPeriodPresentation'
import type { SearchDestinationsPresentation } from '../usecases/SearchDestinations/interfaces/SearchDestinationsPresentation'
import type { GetHouseNextValidPeriodPresentation } from '../usecases/GetHouseNextValidPeriod/interfaces/GetHouseNextValidPeriodPresentation'
import type { SearchHousesSplitMapPresentation } from '../usecases/SearchHousesSplitMap/interfaces/SearchHousesSplitMapPresentation'
import type { GetHouseSplitMapPresentation } from '../usecases/GetHouseSplitMap/interfaces/GetHouseSplitMapPresentation'
import type { GeoCodingSearchType } from '../usecases/SearchUnknownDestination/interfaces/GeoCodingRepository'
import type { FilterType } from 'lc-repository/dist/services'

type Usecases = {
  getHouseAreaEquipments: GetAreaEquipmentsUseCase
  getHouseDetails: GetHouseDetailsUseCase
  getHouseIncludedServices: GetHouseIncludedServices
  getHousePeriods: GetHousePeriodsUseCase
  getHousePeriodsById: GetHousePeriodsByIdUseCase
  getHousePeriodsAndBookings: GetHousePeriodsAndBookingsUseCase
  getHousePhotos: GetHousePhotosUseCase
  getHouseKeyFeatures: GetHouseKeyFeaturesUseCase
  getTopDestinations: GetTopDestinationsUseCase
  makeHouseRequest: MakeHouseRequestUseCase
  orderFacets: OrderRetrievedFacetsUseCase
  searchHouses: {
    searchHousesWithoutDates: SearchHousesWithoutDatesUseCase
    searchHousesWithDates: SearchHousesWithDatesUseCase
  }
  searchHousesV2: {
    searchHousesWithoutDates: SearchHousesWithoutDatesV2UseCase
    searchHousesWithDates: SearchHousesWithDatesUseCase
  }
  getHouseSplitMap: GetHouseSplitMapUseCase
  searchHousesSplitMap: SearchHousesSplitMapUseCase
  searchHousesAndDestinations: SearchHousesAndDestinationsUseCase
  updateInquiry: UpdateInquiryUseCase
  getNbHiddenHouses: GetNbHiddenHousesUseCase
  searchHousesMap: SearchHousesMapUseCase
  getHouseTruthLinks: GetHouseTruthLinksUseCase
  getDestinationFavoritePeriod: GetDestinationFavoritePeriodUseCase
  getDestinationPhoto: GetDestinationPhotoUseCase
  getSimilarHousesPricesByPeriod: GetSimilarHousesPricesByPeriodUseCase
  searchDestinations: SearchDestinationsUseCase
  searchUnknownDestination: SearchUnknownDestinationUseCase
  getHouseNextValidPeriod: GetHouseNextValidPeriodUseCase
}

type Presenter = SearchHousesAndDestinationsPresentation &
  GetTopDestinationsPresentation &
  MakeHouseRequestPresentation &
  OrderRetrievedFacetsPresentation &
  GetHouseKeyFeaturesPresentation &
  GetAreaEquipmentsPresentation &
  SearchHousesPresentation &
  SearchHousesSplitMapPresentation &
  GetHouseDetailsPresentation &
  GetHouseIncludedServicesPresentation &
  GetHousePhotosPresentation &
  GetHousePeriodsPresentation &
  GetHousePeriodsByIdPresentation &
  GetHousePeriodsAndBookingsPresentation &
  GetHouseTruthLinksPresentation &
  GetHouseSplitMapPresentation &
  UpdateInquiryPresentation &
  GetNbHiddenHousesPresentation &
  SearchHousesMapPresentation &
  GetDestinationFavoritePeriodPresentation &
  GetDestinationPhotoPresentation &
  GetSimilarHousesPricesByPeriodPresentation &
  SearchDestinationsPresentation &
  GetHouseNextValidPeriodPresentation

export default class SearchController {
  constructor(
    private usecases: Partial<Usecases>,
    private presenter: Presenter,
  ) {}

  async getHouseDetails(input: {
    houseId: string
    locale: Locale
    privateToken?: string
    withKeyfeatures?: boolean
    withCodes?: boolean
  }) {
    const { houseId, locale, privateToken, withKeyfeatures, withCodes } = input
    await this.usecases.getHouseDetails?.execute({
      presenter: this.presenter,
      houseId,
      locale,
      withKeyfeatures,
      withCodes,
      privateToken,
    })
  }
  async getHousePhotos(
    houseId: string,
    pagination: { page: string; per: string },
    privateToken?: string,
    filter?: FilterType,
  ) {
    await this.usecases.getHousePhotos?.execute(
      this.presenter,
      houseId,
      pagination,
      privateToken,
      filter,
    )
  }

  async getTopDestinations(locale: Locale, algoliaIds: SearchType) {
    await this.usecases.getTopDestinations?.execute(
      this.presenter,
      locale,
      algoliaIds,
    )
  }

  orderFacets(facets: FacetsRetrieve, locale: Locale, mode: Mode = 'client') {
    this.usecases.orderFacets?.execute(this.presenter, facets, locale, mode)
  }

  async searchHouses(
    queryModel: QueryModelType,
    locale: Locale,
    currency: SupportedCurrencies | null,
    algoliaIds: SearchType,
    deviceMode: 'web' | 'mobile',
  ) {
    if (queryModel.period) {
      await this.usecases.searchHouses?.searchHousesWithDates.execute(
        this.presenter,
        queryModel,
        deviceMode,
        locale,
        currency,
        algoliaIds,
      )
    } else {
      await this.usecases.searchHouses?.searchHousesWithoutDates.execute(
        this.presenter,
        queryModel,
        deviceMode,
        locale,
        currency,
        algoliaIds,
      )
    }
  }

  async searchHousesV2(
    queryModel: QueryModelType,
    locale: Locale,
    currency: SupportedCurrencies | null,
    algoliaIds: SearchType,
    deviceMode: 'web' | 'mobile',
  ) {
    if (queryModel.period) {
      await this.usecases.searchHousesV2?.searchHousesWithDates.execute(
        this.presenter,
        queryModel,
        deviceMode,
        locale,
        currency,
        algoliaIds,
      )
    } else {
      await this.usecases.searchHousesV2?.searchHousesWithoutDates.execute(
        this.presenter,
        queryModel,
        deviceMode,
        locale,
        currency,
        algoliaIds,
      )
    }
  }

  async searchHousesSplitMap(
    queryModel: QueryModelType,
    locale: Locale,
    currency: SupportedCurrencies | null,
    algoliaIds: SearchType,
    deviceMode: 'web' | 'mobile',
    requestInfo: { lastId: number },
  ) {
    await this.usecases.searchHousesSplitMap?.execute(
      this.presenter,
      queryModel,
      deviceMode,
      locale,
      currency,
      algoliaIds,
      requestInfo,
    )
  }

  async getHouseSplitMap(
    objectType: ObjectType,
    currency: SupportedCurrencies | null,
    algoliaIds: SearchType,
    deviceMode: 'web' | 'mobile',
  ) {
    await this.usecases.getHouseSplitMap?.execute(
      this.presenter,
      objectType,
      deviceMode,
      currency,
      algoliaIds,
    )
  }

  async searchHousesMap(
    input: SearchHousesMapInput,
    algoliaIds: SearchType,
    deviceMode: 'web' | 'mobile',
  ) {
    await this.usecases.searchHousesMap?.execute(
      this.presenter,
      input,
      algoliaIds,
      deviceMode,
    )
  }

  async searchHousesAndDestinations(
    value: string,
    locale: Locale,
    mode: Mode = 'client',
    algoliaIds: SearchType,
  ) {
    await this.usecases.searchHousesAndDestinations?.execute(
      this.presenter,
      value,
      locale,
      mode,
      algoliaIds,
    )
  }

  async getHousePeriods(
    houseId: string,
    period: { startDate: string; endDate: string },
    currency: SupportedCurrencies,
    destinationIds: string[] = [],
  ) {
    await this.usecases.getHousePeriods?.execute(
      this.presenter,
      houseId,
      period,
      currency,
      destinationIds,
    )
  }

  async makeHouseRequest(request: MakeHouseRequestInput) {
    await this.usecases.makeHouseRequest?.execute(this.presenter, request)
  }

  async getHousePeriodsAndBookings(input: {
    houseId: string
    startAt: string
    endAt: string
    privateToken?: string
  }) {
    await this.usecases.getHousePeriodsAndBookings?.execute(
      this.presenter,
      input,
    )
  }

  async updateInquiry(input: UpdateInquiryInput) {
    await this.usecases.updateInquiry?.execute(input, this.presenter)
  }

  async getNbHiddenHouses(
    input: GetNbHiddenHousesInput,
    algoliaIds: SearchType,
  ) {
    await this.usecases.getNbHiddenHouses?.execute(
      this.presenter,
      input,
      algoliaIds,
    )
  }

  async getHouseKeyFeatures(houseId: string, privateToken?: string) {
    await this.usecases.getHouseKeyFeatures?.execute(
      this.presenter,
      houseId,
      privateToken,
    )
  }

  async getHouseAreaEquipments(houseId: string, privateToken?: string) {
    await this.usecases.getHouseAreaEquipments?.execute(
      this.presenter,
      houseId,
      privateToken,
    )
  }

  async getHousePeriodsById(
    houseId: string,
    period: { startDate: string; endDate: string },
    currency?: SupportedCurrencies,
  ) {
    await this.usecases.getHousePeriodsById?.execute(
      this.presenter,
      houseId,
      period,
      currency,
    )
  }

  async getHouseIncludedServices(input: {
    houseId: string | number
    privateToken?: string
  }) {
    await this.usecases.getHouseIncludedServices?.execute(this.presenter, input)
  }

  async getHouseTruthLinks(input: {
    houseId: string | number
    privateToken?: string
  }) {
    await this.usecases.getHouseTruthLinks?.execute(this.presenter, input)
  }

  async getDestinationFavoritePeriod({
    minPercent,
    ...input
  }: {
    destinationId: string[]
    minPercent?: number
    startDate: string
  }) {
    await this.usecases.getDestinationFavoritePeriod?.execute(
      this.presenter,
      input,
      minPercent,
    )
  }

  async getDestinationPhoto(destinationId: string) {
    await this.usecases.getDestinationPhoto?.execute(
      this.presenter,
      destinationId,
    )
  }

  async getSimilarHousePricesByPeriod(
    input: GetSimilarHousesPricesByPeriodInput,
  ) {
    await this.usecases.getSimilarHousesPricesByPeriod?.execute(
      this.presenter,
      input,
    )
  }

  async searchDestinations(algoliaIds: SearchType) {
    await this.usecases.searchDestinations?.execute(algoliaIds, this.presenter)
  }

  async getHouseNextValidPeriod(houseId: string, privateToken?: string) {
    await this.usecases.getHouseNextValidPeriod?.execute(
      this.presenter,
      houseId,
      privateToken,
    )
  }

  async searchUnknownDestination(
    query: string,
    { locale, ...options }: { locale: Locale } & GeoCodingSearchType,
  ) {
    return this.usecases.searchUnknownDestination?.execute(
      query,
      locale,
      options,
    )
  }
}
